import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CalendarClock, Handshake, IndianRupee, Mail, UserCheck } from "lucide-react";
import CareerForm from "@/components/CareerForm";
import { Reveal } from "@/components/Reveal";
import { SITE, scrollToId } from "@/lib/site";

const CAREER_IMG =
  "https://images.unsplash.com/photo-1571260899304-425eee4c7efc?crop=entropy&cs=srgb&fm=jpg&q=85";

const PERKS = [
  {
    icon: IndianRupee,
    title: "Reliable Monthly Payouts",
    desc: "Fixed payout cycle on time, direct bank transfer without commission delays.",
  },
  {
    icon: CalendarClock,
    title: "Flexible Hours & Locations",
    desc: "Choose home tutoring near your locality or digital remote batches from your desk.",
  },
  {
    icon: UserCheck,
    title: "Curated Student Matches",
    desc: "We screen student requirements and match them directly with your subject expertise.",
  },
  {
    icon: Handshake,
    title: "Long-Term Partnerships",
    desc: "Retain batches year-on-year with consistent student engagement since 2016.",
  },
];

const STEPS = [
  { t: "Share your resume", d: "Apply below with your CV — it lands directly with our founding team." },
  { t: "Get matched with batches", d: "We screen student requirements and match batches to your subjects, boards and timings." },
  { t: "Start teaching & earn", d: "Take batches at your pace and grow a predictable monthly income with us." },
];

const heroLines = [
  <>Teach with purpose.</>,
  <>
    Earn on <em className="italic text-gold">your terms.</em>
  </>,
];

export default function Careers() {
  const parallaxRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: parallaxRef,
    offset: ["start end", "end start"],
  });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [50, -50]);

  return (
    <main data-testid="careers-page">
      {/* ---------- HERO ---------- */}
      <section className="grain relative overflow-hidden bg-navy-dark pb-20 pt-28 text-white sm:pt-32 lg:pb-28 lg:pt-36">
        <div className="pointer-events-none absolute -left-32 bottom-0 h-[420px] w-[420px] rounded-full bg-navy-light/40 blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.span
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3.5 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-gold sm:text-xs"
            >
              For Teachers — Teach With M²
            </motion.span>

            <h1 className="mt-7 font-serif text-4xl font-extrabold leading-[1.06] tracking-tight sm:text-5xl lg:text-6xl xl:text-[4.2rem]">
              {heroLines.map((line, i) => (
                <span key={i} className="block overflow-hidden pb-1">
                  <motion.span
                    className="block"
                    initial={{ y: "112%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 1, delay: 0.15 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="mt-6 max-w-xl text-sm leading-relaxed text-white/70 sm:text-base lg:text-lg"
            >
              Join a network that has connected dedicated educators with
              motivated students since 2016. Focus on teaching — we bring the
              batches to you.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.75 }}
              className="mt-9 inline-flex flex-col gap-4 rounded-3xl border border-gold/30 bg-gold/10 p-7 backdrop-blur sm:flex-row sm:items-center sm:gap-8"
            >
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
                  Monthly Earning Potential
                </p>
                <p className="mt-2 font-serif text-3xl font-extrabold tracking-tight sm:text-4xl">
                  ₹15,000 – ₹40,000+
                </p>
                <p className="mt-1 text-xs text-white/60">based on batches taken</p>
              </div>
              <button
                data-testid="careers-apply-cta-btn"
                onClick={() => scrollToId("apply")}
                className="shrink-0 rounded-full bg-gold px-6 py-3 text-sm font-bold text-navy-dark shadow-lg transition-all hover:-translate-y-0.5 hover:brightness-110"
              >
                Apply With Your Resume
              </button>
            </motion.div>
          </div>

          <div className="lg:col-span-5" ref={parallaxRef}>
            <motion.div
              style={{ y: parallaxY }}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative mx-auto max-w-md"
            >
              <div className="absolute -right-5 -top-5 h-full w-full rounded-[2.2rem] border border-gold/40" />
              <img
                src={CAREER_IMG}
                alt="Teacher in a coaching classroom"
                className="relative aspect-[4/5] w-full rounded-[2.2rem] object-cover shadow-[0_40px_80px_-30px_rgba(0,0,0,0.6)]"
              />
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -left-4 bottom-10 flex items-center gap-2 rounded-2xl border border-slate-100 bg-white/95 px-4 py-3 shadow-xl backdrop-blur sm:-left-8"
              >
                <IndianRupee className="h-4 w-4 text-teal-600" />
                <span className="text-xs font-bold text-navy-dark">Prompt Monthly Payouts</span>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ---------- PERKS ---------- */}
      <section className="py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-navy">
              Why Teach With M²
            </span>
            <h2 className="mt-4 font-serif text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              You bring the passion. We bring the students.
            </h2>
          </Reveal>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {PERKS.map((p, i) => (
              <Reveal key={p.title} delay={0.07 * i}>
                <div className="group h-full rounded-3xl border border-slate-200 bg-white p-7 shadow-sm transition-all hover:-translate-y-1 hover:border-navy/25 hover:shadow-xl">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#EEF4FF] transition-colors group-hover:bg-navy">
                    <p.icon className="h-5 w-5 text-navy transition-colors group-hover:text-white" />
                  </span>
                  <h3 className="mt-5 font-serif text-lg font-bold text-slate-900">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- HOW IT WORKS ---------- */}
      <section className="bg-white py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-navy">
              How It Works
            </span>
            <h2 className="mt-4 font-serif text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              Three steps to your first batch.
            </h2>
          </Reveal>

          <div className="mt-12 grid gap-10 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <Reveal key={s.t} delay={0.1 * i}>
                <div className="relative border-t-2 border-navy/15 pt-6">
                  <span className="absolute -top-[2px] left-0 h-[2px] w-16 bg-gold" />
                  <span className="font-serif text-4xl font-extrabold text-navy/15">
                    0{i + 1}
                  </span>
                  <h3 className="mt-3 font-serif text-xl font-bold text-slate-900">{s.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- APPLY ---------- */}
      <section id="apply" className="bg-dot-grid py-20 sm:py-24 lg:py-28">
        <div className="mx-auto grid max-w-7xl items-start gap-12 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-navy">
              Teacher Application
            </span>
            <h2 className="mt-4 font-serif text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              Send your resume. We do the rest.
            </h2>
            <p className="mt-5 max-w-lg text-sm leading-relaxed text-slate-600 sm:text-base">
              Our founding team personally reviews every application. Shortlisted
              teachers hear back within 48 hours with matching batch options.
            </p>

            <div className="mt-8 space-y-4">
              {[
                "Resume reviewed by the founding team, not a bot",
                "Batches matched to your subjects, boards & timings",
                "Stay in our network for future openings — teachers can always reconnect",
              ].map((p) => (
                <div key={p} className="flex items-start gap-3 text-sm font-semibold text-slate-700">
                  <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gold-light">
                    <span className="h-2 w-2 rounded-full bg-gold" />
                  </span>
                  {p}
                </div>
              ))}
            </div>

            <div className="mt-10 rounded-3xl border border-slate-200 bg-white p-7 shadow-sm">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                Rather email us directly?
              </p>
              <a
                href={SITE.emailHref}
                data-testid="careers-mail-card-link"
                className="mt-3 flex items-center gap-3 break-all font-serif text-lg font-bold text-navy-dark transition hover:text-gold sm:text-xl"
              >
                <Mail className="h-5 w-5 shrink-0 text-gold" />
                {SITE.email}
              </a>
              <p className="mt-3 text-sm text-slate-500">
                Or call <a href={SITE.phoneHref} data-testid="careers-call-card-link" className="font-bold text-navy-dark">{SITE.phoneDisplay}</a>
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="rounded-3xl border border-slate-200 bg-white p-7 shadow-[0_24px_60px_-30px_rgba(15,35,68,0.25)] sm:p-9">
              <CareerForm />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
